/// <reference types="vite/client" />

import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import qs from 'qs'

/**
 * Un appel en `responseType: 'blob'` reçoit aussi le corps des erreurs en `Blob` : le
 * message métier du serveur devient alors illisible pour `getApiErrorMessage`, et les
 * écrans retombent sur leur message générique. On le réhydrate ici, une fois pour toutes
 * les mutations de téléchargement, plutôt que dans chacune d'elles.
 *
 * Silencieux en cas d'échec : un corps binaire ou vide n'est pas une anomalie, l'erreur
 * d'origine suit son cours inchangée.
 */
interface BlobErrorCandidate {
  response?: { data?: unknown }
}

/**
 * `location` et `localStorage` sont déclarés par la lib DOM, mais ce client tourne
 * aussi hors navigateur (rendu serveur, tests Node) où ils sont absents. On relit
 * donc `globalThis` sous un contrat qui les rend optionnels, plutôt que de faire
 * confiance au type ambiant.
 */
const browserGlobals: Partial<Pick<typeof globalThis, 'localStorage' | 'location'>> = globalThis

interface PersistedAuthState {
  state?: {
    admin?: {
      accessToken?: string
    }
    user?: {
      accessToken?: string
    }
  }
}

/**
 * Requête d'origine relue par l'intercepteur 401 pour la rejouer après refresh.
 * `_retry` est notre marqueur : il empêche une boucle de rafraîchissement.
 */
interface RetryableRequestConfig extends Omit<AxiosRequestConfig, 'headers'> {
  _retry?: boolean
  /**
   * Réduites à des en-têtes bruts : l'intercepteur ne fait qu'y réinjecter
   * `Authorization`, et un `AxiosHeaders` (itérable) ne se recopie pas par spread.
   */
  headers?: Record<string, string>
}

type SessionKind = 'admin' | 'user'

const CLIENT_ID_STORAGE_KEY = 'massilia.clientId'

/**
 * Gardé aussi en mémoire : si le stockage est inaccessible (Safari privé,
 * cookies bloqués), l'identifiant doit au moins rester stable le temps de
 * l'onglet — en régénérer un par requête ferait gonfler la table des compteurs
 * du serveur et rendrait le quota inopérant.
 */
let inMemoryClientId: null | string = null

/**
 * `randomUUID` manque hors contexte sécurisé (http://, vieux Safari). Le repli
 * n'a pas besoin d'être cryptographique : l'identifiant ne sert qu'à distinguer
 * deux postes, pas à authentifier qui que ce soit.
 */
const generateClientId = () =>
  'crypto' in globalThis && 'randomUUID' in globalThis.crypto
    ? globalThis.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 15)}`

/**
 * Identifiant stable du navigateur, envoyé en `X-Client-Id` sur chaque appel.
 *
 * Il sert aux quotas du chat côté serveur : comptés par IP, ils confondaient les
 * dix postes d'une agence derrière un même NAT en un seul visiteur qui se
 * partageait un unique quota. Ce n'est pas un identifiant de confiance — la page
 * peut le réécrire — d'où le plafond par IP conservé côté serveur.
 */
export const getClientId = (): string => {
  if (inMemoryClientId !== null) {
    return inMemoryClientId
  }

  try {
    const stored = browserGlobals.localStorage?.getItem(CLIENT_ID_STORAGE_KEY)
    if (stored) {
      inMemoryClientId = stored
      return stored
    }
  } catch {
    /* stockage inaccessible : on retombe sur l'identifiant en mémoire */
  }

  const generated = generateClientId()
  inMemoryClientId = generated
  try {
    browserGlobals.localStorage?.setItem(CLIENT_ID_STORAGE_KEY, generated)
  } catch {
    /* idem : ne jamais faire échouer la requête pour ça */
  }

  return generated
}

const hydrateBlobErrorBody = async (error: BlobErrorCandidate): Promise<void> => {
  const response = error.response
  if (!response || globalThis.Blob === undefined || !(response.data instanceof Blob)) return

  try {
    response.data = JSON.parse(await response.data.text()) as unknown
  } catch {
    /* corps non-JSON : on laisse le Blob en place */
  }
}

class ApiClient {
  private static instance: ApiClient | null = null
  public client: AxiosInstance
  private accessToken: null | string = null
  private refreshPromise: null | Promise<string> = null

  private constructor() {
    const apiUrl = import.meta?.env?.VITE_API_URL
      ? import.meta.env.VITE_API_URL
      : 'http://localhost:3333'

    this.client = axios.create({
      baseURL: apiUrl,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      timeout: 30_000,
      withCredentials: true,
    })

    this.client.interceptors.request.use((config) => {
      config.headers = config.headers ?? {}
      config.headers['X-Client-Id'] = getClientId()

      // Un appel qui pose lui-même son `Authorization` a une bonne raison : la
      // page publique d'un devis porte un jeton de portée `quote`, et l'écraser
      // par le jeton de session ferait échouer le guard pour un client pourtant
      // connecté — qui consulte le lien reçu par mail plutôt que son espace.
      if (this.accessToken && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${this.accessToken}`
      }

      return config
    })

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        await hydrateBlobErrorBody(error)

        const status = error.response?.status as number | undefined
        const originalConfig = error.config as RetryableRequestConfig | undefined
        const requestUrl = String(originalConfig?.url ?? '')

        if (
          status !== 401 ||
          !originalConfig ||
          originalConfig._retry ||
          requestUrl.endsWith('/auth/refresh') ||
          requestUrl.endsWith('/admin/auth/refresh') ||
          requestUrl.endsWith('/auth/login') ||
          requestUrl.endsWith('/admin/auth/login')
        ) {
          throw error
        }

        originalConfig._retry = true

        try {
          const refreshedAccessToken = await this.refreshAccessToken()
          originalConfig.headers = {
            ...originalConfig.headers,
            Authorization: `Bearer ${refreshedAccessToken}`,
          }

          return await this.client.request(originalConfig)
        } catch (refreshError) {
          this.clearAuth()
          const { location } = browserGlobals
          if (location) {
            location.href = '/login'
          }
          throw refreshError
        }
      }
    )
  }

  public static getInstance() {
    ApiClient.instance ??= new ApiClient()
    return ApiClient.instance
  }

  public async bootstrapAccessToken(kind: 'admin' | 'auto' | 'user' = 'auto') {
    let endpoints: string[]
    if (kind === 'admin') {
      endpoints = ['/admin/auth/refresh']
    } else if (kind === 'user') {
      endpoints = ['/auth/refresh']
    } else {
      endpoints = ['/admin/auth/refresh', '/auth/refresh']
    }

    for (const endpoint of endpoints) {
      try {
        const response = await this.client.post<{ accessToken: string }>(endpoint)
        const token = response.data.accessToken
        this.setAccessToken(token)
        return token
      } catch {
        continue
      }
    }

    this.clearAccessToken()
    return null
  }

  public clearAccessToken() {
    this.accessToken = null
  }

  public getAccessToken() {
    return this.accessToken
  }

  public setAccessToken(token: string) {
    this.accessToken = token
  }

  private clearAuth() {
    const storage = this.getStorage()
    this.clearAccessToken()
    if (!storage) {
      return
    }

    storage.removeItem('auth')
  }

  private getPersistedAuthState() {
    const storage = this.getStorage()

    if (!storage) {
      return null
    }

    const persistedAuth = storage.getItem('auth')

    if (!persistedAuth) {
      return null
    }

    try {
      return JSON.parse(persistedAuth) as PersistedAuthState
    } catch {
      return null
    }
  }

  private getSessionKind(): null | SessionKind {
    const state = this.getPersistedAuthState()?.state
    if (state?.admin) {
      return 'admin'
    }

    if (state?.user) {
      return 'user'
    }

    return null
  }

  private getStorage() {
    return browserGlobals.localStorage
  }

  private async refreshAccessToken() {
    if (!this.refreshPromise) {
      this.refreshPromise = this.requestRefreshToken()
    }

    try {
      return await this.refreshPromise
    } finally {
      this.refreshPromise = null
    }
  }

  private async requestRefreshToken() {
    const sessionKind = this.getSessionKind()

    if (!sessionKind) {
      throw new Error('No active session available for refresh')
    }

    const endpoint = sessionKind === 'admin' ? '/admin/auth/refresh' : '/auth/refresh'
    const response = await this.client.post<{ accessToken: string }>(endpoint)
    const accessToken = response.data.accessToken

    this.setAccessToken(accessToken)
    return accessToken
  }
}

export const apiClient = ApiClient.getInstance()

export const bootstrapAccessToken = async (kind: 'admin' | 'auto' | 'user' = 'auto') =>
  apiClient.bootstrapAccessToken(kind)

export const clearAccessToken = () => apiClient.clearAccessToken()

export const getAccessToken = () => apiClient.getAccessToken()

export const setAccessToken = (token: string) => apiClient.setAccessToken(token)
